/**
 * Client-side photo compression (CLAUDE.md field constraints).
 *
 * Photos are reduced to ≤1200px on the long edge and roughly ≤200KB WebP *before*
 * they are queued. A 5MB phone original on a 3G link in a basement would otherwise
 * sit in the outbox for minutes and drain the battery trying.
 */

import { api, ApiError } from './api';

export const MAX_EDGE_PX = 1200;
export const TARGET_BYTES = 200 * 1024;

/** Quality ladder. Each step is tried until the result fits the budget. */
const QUALITY_STEPS = [0.8, 0.65, 0.5, 0.4];

export interface CompressedImage {
  blob: Blob;
  width: number;
  height: number;
  /** False when the browser cannot encode WebP and JPEG was used instead. */
  isWebp: boolean;
}

function scaledSize(width: number, height: number): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (longEdge <= MAX_EDGE_PX) return { width, height };

  const ratio = MAX_EDGE_PX / longEdge;
  return { width: Math.round(width * ratio), height: Math.round(height * ratio) };
}

async function loadBitmap(file: Blob): Promise<ImageBitmap> {
  // createImageBitmap decodes off the main thread, which matters on a slow phone.
  return createImageBitmap(file);
}

function encode(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Returns the original untouched if it is already small enough, so a photo is never
 * re-encoded — and degraded — for no benefit.
 */
export async function compressImage(file: File): Promise<CompressedImage> {
  const bitmap = await loadBitmap(file);
  const { width, height } = scaledSize(bitmap.width, bitmap.height);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas 2D context unavailable');
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  for (const quality of QUALITY_STEPS) {
    const blob = await encode(canvas, 'image/webp', quality);
    if (blob && blob.type === 'image/webp' && blob.size <= TARGET_BYTES) {
      return { blob, width, height, isWebp: true };
    }
  }

  // Either WebP is unsupported (older iOS) or the image resists the budget. Take the
  // lowest-quality WebP if we got one, otherwise fall back to JPEG.
  const lastWebp = await encode(canvas, 'image/webp', QUALITY_STEPS.at(-1)!);
  if (lastWebp && lastWebp.type === 'image/webp') {
    return { blob: lastWebp, width, height, isWebp: true };
  }

  const jpeg = await encode(canvas, 'image/jpeg', 0.6);
  if (!jpeg) throw new Error('Could not encode the photo');

  return { blob: jpeg, width, height, isWebp: false };
}

/**
 * Uploads one already-compressed image and returns its attachment id.
 *
 * Throws `ApiError` with the server's status when the server answered, and whatever
 * `fetch` rejected with — a bare `TypeError` — when it never left the device. Callers
 * need that difference: "too large" is worth retaking, "no signal" is worth retrying,
 * and until this went through `request` they read as the same sentence.
 */
export async function uploadAttachment(blob: Blob, filename: string): Promise<string> {
  const form = new FormData();
  form.append('file', blob, filename);

  const { id } = await api.uploadAttachment(form);
  return id;
}

/**
 * Which message explains a failed upload.
 *
 * One generic string covered every cause before this: a photo the server refused as too
 * large, a file it refused as not really an image, an expired session and a dead plant-room
 * signal all read identically, and only some of those are worth trying again.
 */
export function uploadFailureKey(caught: unknown): string {
  if (caught instanceof ApiError) {
    // 413 is multer's file-size backstop (6MB) — compression should make this unreachable,
    // so seeing it means the photo did not compress, not that the server is fussy.
    if (caught.status === 413) return 'upload.tooLarge';
    // The server sniffs magic bytes and refuses a file whose contents contradict its type.
    if (caught.status === 400) return 'upload.rejected';
    if (caught.status === 401) return 'upload.signedOut';
    return 'upload.failed';
  }

  // `fetch` rejects with TypeError when the request never reached a server at all.
  if (caught instanceof TypeError || !navigator.onLine) return 'upload.offline';
  return 'upload.failed';
}
